import compact from 'lodash/compact';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import sumBy from 'lodash/sumBy';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';
import xor from 'lodash/xor';

import { EntityType } from '../fileUtils';
import { type SystemInternalCommentDetails } from '../internalCommentUtils';
import { roundAtSecondDecimal } from '../numbers';
import { isNilOrEmpty } from '../objectUtils';
import {
  CustomValue,
  Invoice,
  InvoiceDispute,
  InvoiceDisputeLiftCause,
  InvoiceDisputeScope,
  InvoiceDisputeStatus,
  InvoiceStatus,
  InvoiceWithPostings,
  Posting,
} from '../types';
import { InternalCommentSystemMessage } from '../types/internalComment';

import {
  attachInvoiceToPostings,
  calculateAmountLeftToPayOfInvoice,
  getPostingsWithAmountLeftToPay,
} from './invoiceHelper';
import { readDecisionIds } from './reconciliationDecisions';

const DOCUMENT_DISPUTE_KEY = 'document';

type DisputableInvoice = Pick<Invoice, 'disputes' | 'disputeStatus' | 'disputedPostingIds'>;

export interface InvoiceDisputeState {
  disputes: InvoiceDispute[];
  disputeStatus: InvoiceDisputeStatus | null;
  disputedPostingIds: string[];
}

export interface DisputedLine {
  postingId: string;
  label?: string | null;
}

export interface OpenInvoiceDisputeRequest {
  decisionId: string;
  lines?: DisputedLine[] | null;
  reason: string;
  comment?: string | null;
  disputedOn: string;
  openedAt: string;
  openedById?: string | null;
}

export interface LiftInvoiceDisputeRequest {
  decisionId: string;
  disputeIds?: string[] | null;
  liftedAt: string;
  liftedById?: string | null;
  cause: InvoiceDisputeLiftCause;
}

export interface InvoiceSettlement {
  isDocumentSettled: boolean;
  settledPostingIds: string[];
}

export enum InvoiceDisputeRejection {
  REASON_REQUIRED = 'REASON_REQUIRED',
  NOT_A_LEASE_DOCUMENT = 'NOT_A_LEASE_DOCUMENT',
  CREDIT_NOTE = 'CREDIT_NOTE',
  NOT_PAYABLE = 'NOT_PAYABLE',
  LINE_NOT_OPEN = 'LINE_NOT_OPEN',
}

const NOT_PAYABLE_STATUSES = [InvoiceStatus.DRAFT, InvoiceStatus.REVERSED];

export const isDisputeOpen = (dispute: InvoiceDispute): boolean => isNil(dispute.liftedAt);

export const getOpenDisputes = (invoice: DisputableInvoice): InvoiceDispute[] =>
  (invoice.disputes ?? []).filter(isDisputeOpen);

export const buildDisputeId = (decisionId: string, postingId?: string | null): string =>
  `${decisionId}#${postingId ?? DOCUMENT_DISPUTE_KEY}`;

export const getInvoiceDisputeState = (disputes: InvoiceDispute[]): InvoiceDisputeState => {
  const openDisputes = disputes.filter(isDisputeOpen);
  const disputedPostingIds = uniq(
    openDisputes.reduce<string[]>((postingIds, dispute) => {
      if (dispute.scope === InvoiceDisputeScope.LINE && !isNil(dispute.postingId)) {
        postingIds.push(dispute.postingId);
      }
      return postingIds;
    }, [])
  );
  const isDocumentDisputed = openDisputes.some((dispute) => dispute.scope === InvoiceDisputeScope.DOCUMENT);
  const getStatus = (): InvoiceDisputeStatus | null => {
    if (isDocumentDisputed) {
      return InvoiceDisputeStatus.DOCUMENT;
    }
    return isEmpty(disputedPostingIds) ? null : InvoiceDisputeStatus.LINES;
  };
  return { disputes, disputeStatus: getStatus(), disputedPostingIds };
};

export const INVOICE_DISPUTE_DECISION_IDS = 'disputeDecisionIds';
const MAX_TRACKED_DISPUTE_DECISION_IDS = 200;

// A request that changed nothing still consumes its key, so a late replay stays a no-op
export const hasInvoiceDisputeDecision = (
  invoice: DisputableInvoice & Pick<Invoice, 'customValues'>,
  decisionId: string
): boolean =>
  readDecisionIds(invoice.customValues, INVOICE_DISPUTE_DECISION_IDS).includes(decisionId) ||
  (invoice.disputes ?? []).some(
    (dispute) => dispute.openDecisionId === decisionId || dispute.liftDecisionId === decisionId
  );

export const withInvoiceDisputeDecision = (
  customValues: CustomValue[] | null | undefined,
  decisionId: string
): CustomValue[] => {
  const decisionIds = [...readDecisionIds(customValues, INVOICE_DISPUTE_DECISION_IDS), decisionId].slice(
    -MAX_TRACKED_DISPUTE_DECISION_IDS
  );
  return [
    ...(customValues ?? []).filter((customValue) => customValue.name !== INVOICE_DISPUTE_DECISION_IDS),
    { name: INVOICE_DISPUTE_DECISION_IDS, value: JSON.stringify(decisionIds) },
  ];
};

export const openInvoiceDisputes = (
  invoice: DisputableInvoice,
  { decisionId, lines, reason, comment, disputedOn, openedAt, openedById }: OpenInvoiceDisputeRequest
): InvoiceDisputeState => {
  const disputes = invoice.disputes ?? [];
  const openDisputes = getOpenDisputes(invoice);
  const buildDispute = (scope: InvoiceDisputeScope, line: DisputedLine | null): InvoiceDispute => ({
    id: buildDisputeId(decisionId, line?.postingId),
    scope,
    postingId: line?.postingId ?? null,
    postingLabel: isNilOrEmpty(line?.label?.trim()) ? null : line?.label?.trim(),
    reason: reason.trim(),
    comment: isNilOrEmpty(comment?.trim()) ? null : comment?.trim(),
    disputedOn,
    openedAt,
    openedById: openedById ?? null,
    openDecisionId: decisionId,
    liftedAt: null,
    liftedById: null,
    liftCause: null,
    liftDecisionId: null,
  });
  if (isNilOrEmpty(lines)) {
    const isDocumentAlreadyDisputed = openDisputes.some((dispute) => dispute.scope === InvoiceDisputeScope.DOCUMENT);
    return getInvoiceDisputeState(
      isDocumentAlreadyDisputed ? disputes : [...disputes, buildDispute(InvoiceDisputeScope.DOCUMENT, null)]
    );
  }
  const alreadyDisputedPostingIds = getInvoiceDisputeState(disputes).disputedPostingIds;
  const newDisputes = uniqBy(lines, 'postingId').reduce<InvoiceDispute[]>((acc, line) => {
    if (!alreadyDisputedPostingIds.includes(line.postingId)) {
      acc.push(buildDispute(InvoiceDisputeScope.LINE, line));
    }
    return acc;
  }, []);
  return getInvoiceDisputeState([...disputes, ...newDisputes]);
};

const liftDisputes = (
  invoice: DisputableInvoice,
  shouldLift: (dispute: InvoiceDispute) => boolean,
  { decisionId, liftedAt, liftedById, cause }: Omit<LiftInvoiceDisputeRequest, 'disputeIds'>
): InvoiceDisputeState =>
  getInvoiceDisputeState(
    (invoice.disputes ?? []).map((dispute) =>
      isDisputeOpen(dispute) && shouldLift(dispute)
        ? { ...dispute, liftedAt, liftedById: liftedById ?? null, liftCause: cause, liftDecisionId: decisionId }
        : dispute
    )
  );

export const liftInvoiceDisputes = (
  invoice: DisputableInvoice,
  { disputeIds, ...lift }: LiftInvoiceDisputeRequest
): InvoiceDisputeState =>
  liftDisputes(invoice, (dispute) => isNilOrEmpty(disputeIds) || (disputeIds ?? []).includes(dispute.id), lift);

export const liftSettledInvoiceDisputes = (
  invoice: DisputableInvoice,
  { isDocumentSettled, settledPostingIds }: InvoiceSettlement,
  lift: Omit<LiftInvoiceDisputeRequest, 'disputeIds' | 'cause'>
): InvoiceDisputeState | undefined => {
  const shouldLift = (dispute: InvoiceDispute): boolean =>
    isDocumentSettled ||
    (dispute.scope === InvoiceDisputeScope.LINE &&
      !isNil(dispute.postingId) &&
      settledPostingIds.includes(dispute.postingId));
  if (!getOpenDisputes(invoice).some(shouldLift)) {
    return undefined;
  }
  return liftDisputes(invoice, shouldLift, { ...lift, cause: InvoiceDisputeLiftCause.RECONCILED });
};

export const isInvoiceDocumentDisputed = (invoice: Pick<Invoice, 'disputeStatus'>): boolean =>
  invoice.disputeStatus === InvoiceDisputeStatus.DOCUMENT;

export const hasOpenInvoiceDispute = (invoice: Pick<Invoice, 'disputeStatus'>): boolean =>
  !isNil(invoice.disputeStatus);

type DisputeActivityFields = Pick<Invoice, 'disputeStatus' | 'paid' | 'status' | '_deleted'>;

export const isInvoiceDisputeActive = (invoice: DisputeActivityFields): boolean =>
  hasOpenInvoiceDispute(invoice) &&
  !invoice.paid &&
  !NOT_PAYABLE_STATUSES.includes(invoice.status) &&
  !invoice._deleted;

// An open dispute also stops freezing once its document is no longer owed: paid without a lift (Stripe)
export const hasInvoiceDisputeEffectChanged = (
  previous: DisputeActivityFields & Pick<Invoice, 'disputedPostingIds'>,
  next: DisputeActivityFields & Pick<Invoice, 'disputedPostingIds'>
): boolean =>
  (previous.disputeStatus ?? null) !== (next.disputeStatus ?? null) ||
  xor(previous.disputedPostingIds ?? [], next.disputedPostingIds ?? []).length > 0 ||
  (hasOpenInvoiceDispute(next) && isInvoiceDisputeActive(previous) !== isInvoiceDisputeActive(next));

export const isPostingAllocatableUnderDispute = (
  posting: Pick<Posting, 'id'>,
  invoice: Pick<Invoice, 'disputeStatus' | 'disputedPostingIds'>
): boolean => !isInvoiceDocumentDisputed(invoice) && !(invoice.disputedPostingIds ?? []).includes(posting.id);

export const isLeaseFrozenByDispute = (invoices: DisputeActivityFields[]): boolean =>
  invoices.some((invoice) => isInvoiceDisputeActive(invoice) && isInvoiceDocumentDisputed(invoice));

export const getLeaseDisputeStatus = (invoices: DisputeActivityFields[]): InvoiceDisputeStatus | null => {
  const activeInvoices = invoices.filter(isInvoiceDisputeActive);
  if (activeInvoices.some(isInvoiceDocumentDisputed)) {
    return InvoiceDisputeStatus.DOCUMENT;
  }
  return isEmpty(activeInvoices) ? null : InvoiceDisputeStatus.LINES;
};

export const withInvoiceOnPostings = (invoice: InvoiceWithPostings): Posting[] =>
  attachInvoiceToPostings(invoice.postings, invoice);

// The amount still claimed, not the original one: a line disputed after a partial payment removes only the rest
export const getDisputedOpenAmount = (invoice: InvoiceWithPostings): number => {
  if (!isInvoiceDisputeActive(invoice)) {
    return 0;
  }
  const postings = withInvoiceOnPostings(invoice);
  if (isInvoiceDocumentDisputed(invoice)) {
    return calculateAmountLeftToPayOfInvoice(invoice, postings);
  }
  const disputedPostingIds = invoice.disputedPostingIds ?? [];
  return roundAtSecondDecimal(
    sumBy(
      getPostingsWithAmountLeftToPay(postings, true).filter((posting) => disputedPostingIds.includes(posting.id)),
      'totalAmount'
    )
  );
};

export const getUndisputedInvoicePart = (invoice: InvoiceWithPostings): InvoiceWithPostings | undefined => {
  if (!isInvoiceDisputeActive(invoice)) {
    return invoice;
  }
  if (isInvoiceDocumentDisputed(invoice)) {
    return undefined;
  }
  const disputedPostingIds = invoice.disputedPostingIds ?? [];
  return {
    ...invoice,
    amount: roundAtSecondDecimal(invoice.amount - getDisputedOpenAmount(invoice)),
    postings: invoice.postings.filter((posting) => !disputedPostingIds.includes(posting.id)),
  };
};

export const getUndisputedInvoiceParts = (invoices: InvoiceWithPostings[]): InvoiceWithPostings[] =>
  compact(invoices.map(getUndisputedInvoicePart));

export const getDisputablePostingIds = (invoice: InvoiceWithPostings): string[] =>
  getPostingsWithAmountLeftToPay(withInvoiceOnPostings(invoice), true).reduce<string[]>((acc, posting) => {
    if (posting.totalAmount > 0) {
      acc.push(posting.id);
    }
    return acc;
  }, []);

export const getInvoiceDisputeRejection = (
  invoice: InvoiceWithPostings,
  { lines, reason }: Pick<OpenInvoiceDisputeRequest, 'lines' | 'reason'>
): InvoiceDisputeRejection | undefined => {
  if (isEmpty(reason.trim())) {
    return InvoiceDisputeRejection.REASON_REQUIRED;
  }
  if (isNil(invoice.leaseId)) {
    return InvoiceDisputeRejection.NOT_A_LEASE_DOCUMENT;
  }
  if (invoice.creditNote) {
    return InvoiceDisputeRejection.CREDIT_NOTE;
  }
  if (invoice.paid || NOT_PAYABLE_STATUSES.includes(invoice.status)) {
    return InvoiceDisputeRejection.NOT_PAYABLE;
  }
  const openPostingIds = getDisputablePostingIds(invoice);
  if (isEmpty(openPostingIds)) {
    return InvoiceDisputeRejection.NOT_PAYABLE;
  }
  if ((lines ?? []).some(({ postingId }) => !openPostingIds.includes(postingId))) {
    return InvoiceDisputeRejection.LINE_NOT_OPEN;
  }
  return undefined;
};

export interface InvoiceDisputeSystemComment {
  id: string;
  details: SystemInternalCommentDetails;
}

// Ids are derived from the journal so a redelivered stream record writes each comment once
export const getInvoiceDisputeSystemComments = (
  previous: Pick<Invoice, 'disputes'>,
  next: Pick<Invoice, 'id' | 'disputes' | 'invoiceNumber'>
): InvoiceDisputeSystemComment[] => {
  const previousDisputes = previous.disputes ?? [];
  const document = next.invoiceNumber ?? '';
  const target = { entityId: next.id, entityType: EntityType.INVOICE };
  const openedComments = (next.disputes ?? []).reduce<InvoiceDisputeSystemComment[]>((comments, dispute) => {
    if (previousDisputes.some(({ id }) => id === dispute.id)) {
      return comments;
    }
    const isLine = dispute.scope === InvoiceDisputeScope.LINE;
    comments.push({
      id: `${next.id}#dispute-opened#${dispute.id}`,
      details: {
        ...target,
        systemMessage: isLine
          ? InternalCommentSystemMessage.DISPUTE_OPENED_LINE
          : InternalCommentSystemMessage.DISPUTE_OPENED_DOCUMENT,
        parameters: { document, reason: dispute.reason, ...(isLine ? { line: dispute.postingLabel ?? '' } : {}) },
        content: dispute.comment ?? undefined,
      },
    });
    return comments;
  }, []);
  const liftDecisionIds = uniq(
    (next.disputes ?? []).reduce<string[]>((decisionIds, dispute) => {
      const previousDispute = previousDisputes.find(({ id }) => id === dispute.id);
      const isLiftedNow = !isDisputeOpen(dispute) && (isNil(previousDispute) || isDisputeOpen(previousDispute));
      if (isLiftedNow && !isNil(dispute.liftDecisionId)) {
        decisionIds.push(dispute.liftDecisionId);
      }
      return decisionIds;
    }, [])
  );
  const liftedComments = liftDecisionIds.map((liftDecisionId) => ({
    id: `${next.id}#dispute-lifted#${liftDecisionId}`,
    details: { ...target, systemMessage: InternalCommentSystemMessage.DISPUTE_LIFTED, parameters: { document } },
  }));
  return [...openedComments, ...liftedComments];
};
